import { type SceneConfig, type View } from "@novorender/api";

import type { AppDispatch } from "app/store";
import type { ObjectDataAccess } from "dataAccess";
import { CameraType, type MainObjectPick, renderActions } from "features/render";
import { telemetry } from "telemetry";

import { getOrthoFitClipPlane, getOrthoFromPinhole } from "./utils";

/**
 * The toolbar camera button: perspective to orthographic, or back. Returns false when there is no
 * view or scene yet, so callers only report a toggle that happened.
 */
export function toggleOrthographicProjection({
    dispatch,
    view,
    scene,
    dataAccess,
    cameraType,
    mainObject,
    mainObjectPick,
    telemetrySource,
}: {
    dispatch: AppDispatch;
    view: View | undefined;
    /** `scene.up` is optional; `getOrthoFromPinhole` falls back to Y-up when the scene has none. */
    scene: SceneConfig | undefined;
    dataAccess: ObjectDataAccess | undefined;
    cameraType: CameraType;
    mainObject: number | undefined;
    mainObjectPick: MainObjectPick | null | undefined;
    telemetrySource: string;
}): boolean {
    if (!view || !scene) {
        return false;
    }
    if (cameraType === CameraType.Pinhole) {
        getOrthoFromPinhole(view, { dataAccess, up: scene.up, mainObject })
            .then(async (orthographicParams) => {
                dispatch(
                    renderActions.setCamera({
                        type: CameraType.Orthographic,
                        goTo: { ...orthographicParams, flyTime: 0 },
                    }),
                );
                const clipPlane = await getOrthoFitClipPlane({ view, dataAccess, mainObject, mainObjectPick });
                if (clipPlane) {
                    dispatch(renderActions.addClippingPlane(clipPlane));
                }
            })
            .catch((error) => {
                console.warn("Failed to build orthographic camera:", error);
                telemetry.trackCaughtException({ source: telemetrySource, category: "render", error });
                dispatch(renderActions.setCamera({ type: CameraType.Orthographic }));
            });
    } else {
        dispatch(renderActions.setCamera({ type: CameraType.Pinhole }));
        dispatch(renderActions.removeTemporaryClippingPlanes());
    }
    return true;
}
